"use client";
import React from 'react';
import { ArrowDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from "~/lib/utils";

const tableData = [
  {
    capability: "Intent",
    today: "Goals collide, not optimized",
    withIoc: "Aligned on what success means",
  },
  {
    capability: "Context",
    today: "Each agent in its own silo",
    withIoc: "Shared knowledge and context",
  },
  {
    capability: "Reasoning",
    today: "Solo, or escalated to humans",
    withIoc: "Collective over shared trade-offs",
  },
  {
    capability: "Guardrails",
    today: "Per-agent, after the fact",
    withIoc: "System-wide, at runtime",
  },
  {
    capability: "Innovation",
    today: "Capped at any one agent",
    withIoc: "Emerges system-wide",
  },
  {
    capability: "Communication",
    today: "Messaging only",
    withIoc: "Efficient via semantic understanding",
  },
  {
    capability: "Learnings",
    today: "Reset every session",
    withIoc: "Persist, compound, auditable",
  },
];

const ROTATING_WORDS = ["memory", "reasoning", "intention"];

export function ImpactSection() {
  const [wordIndex, setWordIndex] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false); // start fading out
      
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsVisible(true); // start fading in
      }, 700); // Wait for fade out to complete (0.7s)
      
    }, 2500); // Show each word for a few seconds before cycling

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative z-10 py-16 md:py-24 text-white flex flex-col items-center px-4" style={{ backgroundColor: "#050511" }}>
      <div className="w-full max-w-[896px] mx-auto flex flex-col items-center">
        {/* Title */}
        <h2 className="text-center text-4xl md:text-[48px] font-[350] leading-tight mb-6 text-white">
          <span className="text-2xl md:text-[32px] font-[700] text-[#F5F985] block mb-2">The Impact:</span>
          Agents that have shared and aligned <br className="hidden md:block" />
          <span 
            className="bg-gradient-to-r from-[#00E5FF] to-[#FACC15] text-transparent bg-clip-text font-[400] transition-opacity duration-700 ease-in-out"
            style={{ opacity: isVisible ? 1 : 0 }}
          >
            {ROTATING_WORDS[wordIndex]}
          </span>
        </h2>

        {/* Subtitle */}
        <p className="text-center text-[15.5px] text-[#76767c] font-[400] leading-relaxed max-w-[800px] mb-12">
          The Internet of Cognition unites distributed AI agents through a cognition fabric, cognition engines, and cognition state protocols — enabling aligned intent, context, and reasoning at scale.
        </p>

        {/* Table */}
        <div className="w-full bg-[#0B0F19] rounded-2xl border border-[#1E293B] overflow-hidden mb-12 shadow-2xl shadow-black/50">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#1E293B] bg-[#0D0F1A]">
                <th className="py-6 px-6 md:px-8 text-[12px] md:text-[13px] font-[600] uppercase tracking-wider text-[#606168] w-[25%]">
                  Capability
                </th>
                <th className="py-6 px-6 md:px-8 text-[12px] md:text-[13px] font-[600] uppercase tracking-wider text-[#606168] w-[35%]">
                  Today's Architecture
                </th>
                <th className="py-6 px-6 md:px-8 text-[12px] md:text-[13px] font-[600] uppercase tracking-wider text-[#049fd9] w-[40%]">
                  With IOC
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr 
                  key={row.capability} 
                  className={cn(
                    "transition-colors hover:bg-white/[0.02]",
                    index % 2 === 0 ? "bg-[#0A0C14]" : "bg-[#0D0F1A]",
                    index !== tableData.length - 1 && "border-b border-[#1E293B]"
                  )}
                >
                  <td className="py-5 px-6 md:px-8 text-[14px] md:text-[15px] font-[400] text-[#E2E8F0]">
                    {row.capability}
                  </td>
                  <td className="py-5 px-6 md:px-8 text-[14px] md:text-[15px] font-[400] text-[#64748B]">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-[14px] h-[14px] text-[#EF4444] shrink-0" />
                      <span>{row.today}</span>
                    </div>
                  </td>
                  <td className="py-5 px-6 md:px-8 text-[14px] md:text-[15px] font-[500] text-[#049fd9]">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-[14px] h-[14px] shrink-0 text-[#00E5FF]" />
                      <span>{row.withIoc}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Explore CTA */}
        <button 
          className="flex items-center gap-4 text-[14px] font-[600] text-custom-yellow uppercase tracking-widest hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.preventDefault();
            const target = document.getElementById('use-cases');
            if (target) {
              const offset = target.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: offset, behavior: 'smooth' });
            }
          }}
        >
          <ArrowDown className="w-5 h-5" strokeWidth={2} />
          <span>Explore use cases</span>
          <ArrowDown className="w-5 h-5" strokeWidth={2} />
        </button>
      </div>
    </section>
  );
}
